import { useContext, useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import '../styles/Header.css';
import { CartContext } from '../middleware/TrackCart';

const Header = () => {
  const { cartItems } = useContext(CartContext);
  const [showNotification, setShowNotification] = useState(false);
  const [addedItem, setAddedItem] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const sidebarRef = useRef(null);

  // Get the added item from localStorage if it exists
  useEffect(() => {
    const newItem = localStorage.getItem('lastAddedItem');
    if (newItem) {
      setAddedItem(JSON.parse(newItem));
      setShowNotification(true);
      
      // Clear after showing
      localStorage.removeItem('lastAddedItem');
      
      // Auto hide after 7 seconds (increased from 5 seconds)
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 7000);
      
      return () => clearTimeout(timer);
    }
  }, [cartItems]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target) && 
          !event.target.classList.contains('hamburger') &&
          !event.target.parentElement?.classList.contains('hamburger')) {
        setMobileMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebarRef]);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [mobileMenuOpen]);

  const closeNotification = () => {
    setShowNotification(false);
  };

  const viewCart = () => {
    closeNotification();
    setMobileMenuOpen(false);
    navigate('/cart');
  };

  const continueShopping = () => {
    closeNotification();
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavLinkClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <NavLink to="/">
                <img src="/images/contractwell.png" alt="ContractWell Logo" className="logo-image" />
              </NavLink>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="main-nav desktop-nav">
              <ul>
                <li className="nav-item left-item"><NavLink to="/">Home</NavLink></li>
                <li className="nav-item center-item"><NavLink to="/library">Shop Contracts</NavLink></li>
                <li className="nav-item"><NavLink to="/faq">FAQs</NavLink></li>
                <li className="nav-item right-item"><NavLink to="/blog" style={{ color: 'white' }}>Blog</NavLink></li>
              </ul>
            </nav>
            
            <div className="mobile-controls">
              {/* Cart icon - positioned before hamburger */}
              <div className="search-icons">
                <NavLink to="/cart" className="cart-icon" title={`View your cart (${cartItems ? cartItems.length : 0})`}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 22C9.55228 22 10 21.5523 10 21C10 20.4477 9.55228 20 9 20C8.44772 20 8 20.4477 8 21C8 21.5523 8.44772 22 9 22Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M20 22C20.5523 22 21 21.5523 21 21C21 20.4477 20.5523 20 20 20C19.4477 20 19 20.4477 19 21C19 21.5523 19.4477 22 20 22Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M1 1H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 6H6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {cartItems && cartItems.length > 0 && (
                    <span className="cart-count">{cartItems.length}</span>
                  )}
                </NavLink>
              </div>
              
              {/* Hamburger Menu for Mobile */}
              <button 
                className={`hamburger ${mobileMenuOpen ? 'is-active' : ''}`} 
                onClick={toggleMobileMenu}
                aria-label="Toggle mobile menu"
                aria-expanded={mobileMenuOpen}
              >
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Sidebar */}
      <div className={`mobile-sidebar-overlay ${mobileMenuOpen ? 'is-active' : ''}`} onClick={toggleMobileMenu}></div>
      <div className={`mobile-sidebar ${mobileMenuOpen ? 'is-active' : ''}`} ref={sidebarRef}>
        <div className="mobile-sidebar-header">
          <NavLink to="/" onClick={handleNavLinkClick}>
            <img src="/images/contractwell.png" alt="ContractWell Logo" className="sidebar-logo" />
          </NavLink>
          <button className="close-sidebar" onClick={toggleMobileMenu}>&times;</button>
        </div>
        <nav className="mobile-nav">
          <ul>
            <li><NavLink to="/" onClick={handleNavLinkClick}>Home</NavLink></li>
            <li><NavLink to="/library" onClick={handleNavLinkClick}>Shop Contracts</NavLink></li>
            <li><NavLink to="/faq" onClick={handleNavLinkClick}>FAQs</NavLink></li>
            <li><NavLink to="/blog" onClick={handleNavLinkClick}>Blog</NavLink></li>
            <li className="mobile-cart-link">
              <NavLink to="/cart" onClick={handleNavLinkClick}>
                <span>Cart</span>
                {cartItems && cartItems.length > 0 && (
                  <span className="mobile-cart-count">{cartItems.length}</span>
                )}
              </NavLink>
            </li>
          </ul>
        </nav>
      </div>

      {/* Cart Notification */}
      {showNotification && addedItem && (
        <div className={`cart-notification ${showNotification ? 'visible' : ''}`}>
          <div className="cart-notification-header">
            <h4>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 22C9.55228 22 10 21.5523 10 21C10 20.4477 9.55228 20 9 20C8.44772 20 8 20.4477 8 21C8 21.5523 8.44772 22 9 22Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20 22C20.5523 22 21 21.5523 21 21C21 20.4477 20.5523 20 20 20C19.4477 20 19 20.4477 19 21C19 21.5523 19.4477 22 20 22Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M1 1H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 6H6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Item added to your cart
            </h4>
            <button className="close-notification" onClick={closeNotification}>×</button>
          </div>
          <div className="cart-notification-content">
            <img src={addedItem.image || '/images/contract.png'} alt={addedItem.title} className="cart-item-img" />
            <div className="cart-item-info">
              <h5>{addedItem.title}</h5>
              <p>${addedItem.price?.toFixed(2)}</p>
            </div>
          </div>
          <div className="cart-notification-actions">
            <button className="view-cart-btn btn" onClick={viewCart}>View cart ({cartItems.length})</button>
            <button className="continue-shopping-btn btn" onClick={continueShopping}>Continue shopping</button>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
